import React from "react";
import { pdf } from "@react-pdf/renderer";
import ProviderDetailsPdfDoc from "../components/ReportPdf/ProviderDetailsPdfDoc";
import {
  formatReportPercent,
  REPORT_EMPTY_DISPLAY,
} from "./ReportDashboard";

export const PROVIDER_ENGAGEMENT_PROVIDERS_PAGE_SIZE = 20;
export const PROVIDER_DETAILS_EXPORT_FILE_NAME = "Provider_Details";
export const PROVIDER_DETAILS_DEFAULT_SORT_BY = "providerName";
export const PROVIDER_DETAILS_DEFAULT_SORT_DIRECTION = "ASC";

export const PROVIDER_DETAILS_COLUMNS = [
  { key: "providerName", header: "Provider", sortable: true },
  { key: "patientCount", header: "Patients on Caseload", sortable: true },
  { key: "monitoringTime", header: "Monitoring Time (HH:MM:SS)", sortable: true },
  { key: "patientAdherence", header: "Average Caseload Adherence", sortable: true },
];

export const parseProviderEngagementSummaryResponse = (response) => {
  const data = response?.data?.data ?? response?.data ?? {};
  return {
    totalMonitoringTime: data.totalMonitoringTime ?? null,
    activeProviders: data.activeProviders ?? null,
    avgTimePerProvider: data.avgTimePerProvider ?? null,
    avgTimePerPatient: data.avgTimePerPatient ?? null,
    patientsMonitored: data.patientsMonitored ?? null,
    appliedFilters: data.appliedFilters ?? null,
  };
};

export const SECONDS_PER_HOUR = 3600;

/**
 * Trend API field `monitoringHours` is sent as total seconds.
 * Convert to decimal hours for chart plotting / Y-axis.
 */
export const monitoringSecondsToHours = (seconds) => {
  if (seconds == null || seconds === "") return null;
  const n = Number(seconds);
  if (!Number.isFinite(n)) return null;
  return n / SECONDS_PER_HOUR;
};

/** Format trend monitoring seconds as HH:MM:SS for tooltips. */
export const formatMonitoringSecondsAsHms = (seconds) => {
  if (seconds == null || seconds === "") return REPORT_EMPTY_DISPLAY;
  const n = Number(seconds);
  if (!Number.isFinite(n) || n < 0) return REPORT_EMPTY_DISPLAY;

  const total = Math.floor(n);
  const hours = Math.floor(total / SECONDS_PER_HOUR);
  const minutes = Math.floor((total % SECONDS_PER_HOUR) / 60);
  const secs = total % 60;

  return [
    String(hours).padStart(2, "0"),
    String(minutes).padStart(2, "0"),
    String(secs).padStart(2, "0"),
  ].join(":");
};

export const parseProviderEngagementMonitoringTrendResponse = (response) => {
  const data = response?.data?.data ?? response?.data ?? {};
  const points = Array.isArray(data.points) ? data.points : [];
  return {
    points: points.map((point) => {
      // API sends total seconds in `monitoringHours`
      const monitoringSeconds = point.monitoringHours != null
        ? Number(point.monitoringHours)
        : null;
      return {
        date: point.date ?? null,
        monitoringSeconds: Number.isFinite(monitoringSeconds) ? monitoringSeconds : null,
        monitoringHours: monitoringSecondsToHours(monitoringSeconds),
        patientsMonitored: point.patientsMonitored != null ? Number(point.patientsMonitored) : null,
      };
    }),
    appliedFilters: data.appliedFilters ?? null,
  };
};

export const parseProviderEngagementProvidersPage = (response) => {
  const data = response?.data?.data ?? response?.data ?? {};
  const providers = data.providers ?? {};
  const list = Array.isArray(providers.pageContent) ? providers.pageContent : [];
  const pageNo = providers.pageNo ?? 0;
  const totalPage = providers.totalPage ?? 1;

  return {
    list,
    pageNo,
    pageSize: providers.pageSize ?? PROVIDER_ENGAGEMENT_PROVIDERS_PAGE_SIZE,
    totalPage,
    totalRecords: providers.totalRecords ?? list.length,
    hasMore: pageNo + 1 < totalPage,
    appliedFilters: data.appliedFilters ?? null,
  };
};

const escapeCsvCell = (value) => {
  if (value == null || value === "") return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const triggerFileDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const formatProviderMonitoringTime = (monitoringTime) => {
  if (monitoringTime == null) return REPORT_EMPTY_DISPLAY;
  if (typeof monitoringTime === "string") {
    return monitoringTime || REPORT_EMPTY_DISPLAY;
  }
  if (typeof monitoringTime === "object") {
    if (monitoringTime.formatted) return monitoringTime.formatted;
  }
  return REPORT_EMPTY_DISPLAY;
};

export const mapProviderDetailsForExport = (rows) => (
  Array.isArray(rows) ? rows : []
).map((row) => ({
  providerName: row.providerName != null && row.providerName !== ""
    ? String(row.providerName)
    : REPORT_EMPTY_DISPLAY,
  patientCount: row.patientCount != null ? String(row.patientCount) : REPORT_EMPTY_DISPLAY,
  monitoringTime: formatProviderMonitoringTime(row.monitoringTime),
  patientAdherence: formatReportPercent(row.patientAdherence, REPORT_EMPTY_DISPLAY, 1),
}));

export const convertProviderDetailsToCsv = (rows) => {
  const exportRows = mapProviderDetailsForExport(rows);
  return [
    PROVIDER_DETAILS_COLUMNS.map((col) => col.header).join(","),
    ...exportRows.map((row) => PROVIDER_DETAILS_COLUMNS
      .map((col) => escapeCsvCell(row[col.key]))
      .join(",")),
  ].join("\n");
};

export const downloadProviderDetailsCsv = (rows) => {
  const csvData = convertProviderDetailsToCsv(rows);
  const blob = new Blob([csvData], { type: "text/csv" });
  triggerFileDownload(blob, `${PROVIDER_DETAILS_EXPORT_FILE_NAME}.csv`);
};

export const downloadProviderDetailsPdf = async (rows) => {
  const exportRows = mapProviderDetailsForExport(rows);
  const pdfBlob = await pdf(
    <ProviderDetailsPdfDoc
      data={exportRows}
      columns={PROVIDER_DETAILS_COLUMNS}
    />
  ).toBlob();
  triggerFileDownload(pdfBlob, `${PROVIDER_DETAILS_EXPORT_FILE_NAME}.pdf`);
};
